import { ref, onUnmounted, watch, type Ref } from "vue";
import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";
import { useUserStore } from "@/store/modules/user";

// 全局单例实例
let globalSocketInstance: ReturnType<typeof useSocketIO> | null = null;

/**
 * 全局Socket连接状态
 * @description 用于跟踪当前是否有用户连接到Socket.IO服务器
 */

/**
 * 当前连接的用户ID
 */
let currentUserId: string | null = null;

/**
 * 自动管理状态标志
 */
let autoManagementSetup = false;

/**
 * Socket.IO连接配置选项
 */
export interface SocketIOOptions {
  path?: string;
  autoConnect?: boolean;
  reconnection?: boolean;
  reconnectionAttempts?: number;
  reconnectionDelay?: number;
  transports?: string[];
  query?: Record<string, any>;
  debug?: boolean;
}

/**
 * 事件订阅处理器类型
 */
type EventHandler<T = any> = (data: T) => void;

/**
 * 房间管理接口
 */
interface RoomManager {
  /** 加入房间 */
  join: (room: string) => void;
  /** 离开房间 */
  leave: (room: string) => void;
  /** 获取当前加入的房间列表 */
  getRooms: () => string[];
  /** 检查是否在指定房间 */
  isInRoom: (room: string) => boolean;
}

/**
 * 订阅管理器接口
 */
interface SubscriptionManager {
  /** 订阅事件 */
  subscribe: <T = any>(event: string, handler: EventHandler<T>) => () => void;
  /** 取消订阅 */
  unsubscribe: (event: string, handler?: EventHandler) => void;
  /** 取消所有订阅 */
  unsubscribeAll: (event?: string) => void;
  /** 获取订阅统计 */
  getSubscriptionStats: () => Record<string, number>;
}

/**
 * Socket.IO Hook返回值类型
 */
interface UseSocketIOReturn {
  /** Socket实例 */
  socket: Ref<Socket | null>;
  /** 连接状态 */
  isConnected: Ref<boolean>;
  /** 错误信息 */
  error: Ref<string | null>;
  /** 房间管理器 */
  roomManager: RoomManager;
  /** 订阅管理器 */
  subscriptionManager: SubscriptionManager;
  /** 初始化连接 */
  initSocket: () => void;
  /** 发送消息 */
  send: <T = any>(event: string, data: T) => void;
  /** 断开连接 */
  disconnect: () => void;
  /** 重新连接 */
  reconnect: () => void;
  /** 获取连接URL（用于调试） */
  getUrl: () => string;
}

/**
 * 创建或获取全局Socket.IO实例
 *
 * @description 全局单例模式，确保整个应用只有一个Socket.IO连接
 * @param url Socket.IO服务器URL
 * @param options 连接配置选项
 * @returns Socket.IO连接管理对象
 */
function createGlobalSocketIO(url: string, options: SocketIOOptions = {}): UseSocketIOReturn {
  const socket = ref<Socket | null>(null);
  const isConnected = ref(false);
  const error = ref<string | null>(null);
  const joinedRooms = ref<Set<string>>(new Set());
  const eventSubscriptions = ref<Map<string, Set<EventHandler>>>(new Map());
  const subscriptionStats = ref<Record<string, number>>({});

  const defaultOptions: SocketIOOptions = {
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: 5,
    reconnectionDelay: 1000,
    transports: ["websocket", "polling"],
    debug: import.meta.env.DEV,
    ...options,
  };

  /**
   * 日志输出
   */
  const log = (level: "info" | "warn" | "error", ...args: any[]) => {
    if (defaultOptions.debug) {
      const prefix = `[Socket.IO] ${level.toUpperCase()}:`;
      console[level](prefix, ...args);
    }
  };

  /**
   * 初始化Socket连接
   */
  const initSocket = () => {
    try {
      socket.value = io(url, defaultOptions);

      // 基础事件监听
      socket.value.on("connect", handleConnect);
      socket.value.on("connect_error", handleConnectError);
      socket.value.on("disconnect", handleDisconnect);
      socket.value.on("error", handleError);

      // 监听所有自定义事件
      socket.value.onAny(handleAnyEvent);

      // log("info", "初始化Socket.IO连接:", url, "连接选项:", defaultOptions, "Socket.IO连接初始化完成");
    } catch (err) {
      const errorMsg = `Socket初始化失败: ${err}`;
      error.value = errorMsg;
      log("error", errorMsg);
    }
  };

  /**
   * 处理连接成功事件
   */
  const handleConnect = () => {
    isConnected.value = true;
    error.value = null;
    // log("info", "✅ Socket.IO连接成功", "Socket ID:", socket.value?.id);

    // 连接成功后重新加入之前加入的房间
    joinedRooms.value.forEach((room) => {
      socket.value?.emit("join_room", room);
      // log("info", `重新加入房间: ${room}`);
    });
  };

  /**
   * 处理连接错误事件
   */
  const handleConnectError = (err: Error) => {
    error.value = err.message;
    isConnected.value = false;
    log("error", "❌ Socket.IO连接错误:", err.message);
  };

  /**
   * 处理断开连接事件
   */
  const handleDisconnect = (_reason: string) => {
    isConnected.value = false;
    // log("info", "🔌 Socket.IO断开连接:", _reason);
  };

  /**
   * 处理错误事件
   */
  const handleError = (err: Error) => {
    log("error", "Socket.IO错误:", err);
  };

  /**
   * 处理任意事件
   */
  const handleAnyEvent = (eventName: string, ...args: any[]) => {
    // log("info", `📡 收到事件 [${eventName}]:`, args);

    // 分发给对应的事件订阅者
    const handlers = eventSubscriptions.value.get(eventName);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(args[0]);
        } catch (err) {
          log("error", `事件处理器执行错误 [${eventName}]:`, err);
        }
      });
    }
  };

  /**
   * 发送消息
   */
  const send = <T = any>(event: string, data: T) => {
    if (socket.value && isConnected.value) {
      socket.value.emit(event, data);
      // log("info", `📤 发送事件 [${event}]:`, data);
    } else {
      log("warn", "Socket未连接，无法发送消息");
    }
  };

  /**
   * 断开连接
   */
  const disconnect = () => {
    if (socket.value) {
      socket.value.disconnect();
      socket.value = null;
      isConnected.value = false;
      joinedRooms.value.clear();
      log("info", "Socket.IO连接已断开");
    }
  };

  /**
   * 重新连接
   */
  const reconnect = () => {
    if (socket.value) {
      socket.value.connect();
      log("info", "尝试重新连接Socket.IO");
    }
  };

  /**
   * 获取连接URL
   */
  const getUrl = () => {
    return url;
  };

  // 房间管理器实现
  const roomManager: RoomManager = {
    join: (room: string) => {
      if (socket.value && isConnected.value) {
        socket.value.emit("join_room", room);
        joinedRooms.value.add(room);
        // log("info", `加入房间: ${room}`);
      } else {
        log("warn", "Socket未连接，无法加入房间");
      }
    },

    leave: (room: string) => {
      if (socket.value && isConnected.value) {
        socket.value.emit("leave_room", room);
        joinedRooms.value.delete(room);
        log("info", `离开房间: ${room}`);
      }
    },

    getRooms: () => {
      return Array.from(joinedRooms.value);
    },

    isInRoom: (room: string) => {
      return joinedRooms.value.has(room);
    },
  };

  // 订阅管理器实现
  const subscriptionManager: SubscriptionManager = {
    subscribe: <T = any>(event: string, handler: EventHandler<T>) => {
      if (!eventSubscriptions.value.has(event)) {
        eventSubscriptions.value.set(event, new Set());
        subscriptionStats.value[event] = 0;
      }

      const handlers = eventSubscriptions.value.get(event)!;
      handlers.add(handler as EventHandler);
      subscriptionStats.value[event] = handlers.size;

      // log("info", `订阅事件 [${event}]，当前订阅数: ${subscriptionStats.value[event]}`);

      // 返回取消订阅函数
      return () => {
        subscriptionManager.unsubscribe(event, handler as EventHandler);
      };
    },

    unsubscribe: (event: string, handler?: EventHandler) => {
      const handlers = eventSubscriptions.value.get(event);
      if (handlers) {
        if (handler) {
          handlers.delete(handler);
        } else {
          handlers.clear();
        }

        subscriptionStats.value[event] = handlers.size;

        if (handlers.size === 0) {
          eventSubscriptions.value.delete(event);
          delete subscriptionStats.value[event];
        }

        // log("info", `取消订阅事件 [${event}]，剩余订阅数: ${subscriptionStats.value[event] || 0}`);
      }
    },

    unsubscribeAll: (event?: string) => {
      if (event) {
        subscriptionManager.unsubscribe(event);
      } else {
        eventSubscriptions.value.clear();
        subscriptionStats.value = {};
        log("info", "取消所有事件订阅");
      }
    },

    getSubscriptionStats: () => {
      return { ...subscriptionStats.value };
    },
  };

  // 组件卸载时清理
  onUnmounted(() => {
    disconnect();
    subscriptionManager.unsubscribeAll();
  });

  return {
    socket: socket as Ref<Socket | null>,
    isConnected,
    error,
    roomManager,
    subscriptionManager,
    initSocket,
    send,
    disconnect,
    reconnect,
    getUrl,
  };
}

/**
 * 初始化全局Socket连接
 * @description 在用户登录成功后调用，确保只有一个Socket连接
 */
export function initGlobalSocket(user_id?: string): void {
  // 如果没有提供user_id，尝试从store获取
  if (!user_id) {
    const userStore = useUserStore();
    user_id = userStore.userInfo?.id;
  }

  // 如果仍然没有user_id，无法建立连接
  if (!user_id) {
    console.warn("无法初始化Socket连接：缺少user_id");
    return;
  }

  // 检查user_id是否变化
  if (currentUserId === user_id && globalSocketInstance && globalSocketInstance.isConnected.value) {
    console.log("Socket连接已存在且user_id相同，无需重新连接");
    return;
  }

  // 如果user_id变化或实例不存在，需要重新创建连接
  if (currentUserId !== user_id || !globalSocketInstance) {
    // 先断开现有连接
    if (globalSocketInstance) {
      globalSocketInstance.disconnect();
      globalSocketInstance = null;
    }
    
    const finalUrl = import.meta.env.VITE_API_SOCKET_URL;
    const PATH = import.meta.env.VITE_API_SOCKET_PATH;

    globalSocketInstance = createGlobalSocketIO(finalUrl, {
      path: PATH,
      autoConnect: true,
      query: {
        user_id
      }
    });
    
    // 更新当前用户ID
    currentUserId = user_id;
  }

  // 确保实际建立连接
  if (globalSocketInstance && !globalSocketInstance.isConnected.value) {
    globalSocketInstance.initSocket();
  }

  // console.log("✅ 全局Socket连接初始化完成，user_id:", user_id);
}

/**
 * 断开全局Socket连接
 * @description 在用户退出登录时调用
 */
export function disconnectGlobalSocket(): void {
  if (globalSocketInstance) {
    globalSocketInstance.disconnect();
    globalSocketInstance = null;
  }
  currentUserId = null;
  console.log("🔌 全局Socket连接已断开");
}

/**
 * 设置Socket连接自动管理
 * @description 监听用户登录状态，自动建立和断开Socket连接
 */
export function setupSocketAutoManagement(): void {
  if (autoManagementSetup) {
    console.warn("Socket自动管理已设置");
    return;
  }

  const userStore = useUserStore();

  watch(
    [() => userStore.isLogin, () => userStore.userInfo?.id],
    ([isLogin, userId], [prevIsLogin, prevUserId]) => {
      // 登录状态变化处理
      if (isLogin && userId) {
        // 用户登录且有user_id，建立连接
        if (!globalSocketInstance || !globalSocketInstance.isConnected.value || currentUserId !== userId) {
          // console.log("[Socket自动管理] 触发连接建立，user_id:", userId);
          initGlobalSocket(userId);
        } else {
          console.log("[Socket自动管理] 连接已存在且user_id匹配，无需操作");
        }
      } else if (!isLogin && prevIsLogin) {
        // 用户退出登录，断开连接
        console.log("[Socket自动管理] 用户退出登录，断开连接");
        disconnectGlobalSocket();
      } else if (isLogin && userId && prevUserId !== userId) {
        // user_id变化（如切换账号），重新连接
        console.log("[Socket自动管理] user_id变化，重新连接，新user_id:", userId);
        disconnectGlobalSocket();
        initGlobalSocket(userId);
      } else {
        console.log("[Socket自动管理] 状态变化但无需操作");
      }
    },
    { immediate: true }
  );

  autoManagementSetup = true;
  // console.log("✅ Socket连接自动管理已启动");
}

/**
 * Socket.IO连接管理Hook
 *
 * @description 提供Socket.IO连接的完整生命周期管理，支持房间管理和事件订阅
 * @param url Socket.IO服务器URL（可选，默认使用环境变量配置）
 * @param options 连接配置选项
 * @returns Socket.IO连接管理对象
 */
export function useSocketIO(url?: string, options: SocketIOOptions = {}): UseSocketIOReturn {
  // 使用默认URL或传入的URL
  const finalUrl = url ?? import.meta.env.VITE_API_SOCKET_URL;
  const PATH = import.meta.env.VITE_API_SOCKET_PATH;

  // 如果全局实例不存在，创建新的实例
  if (!globalSocketInstance) {
    globalSocketInstance = createGlobalSocketIO(finalUrl, {
      path: PATH,
      ...options,
    });
  }

  // 检查URL是否匹配，如果不匹配则重新创建
  if (globalSocketInstance.getUrl() !== finalUrl) {
    globalSocketInstance.disconnect();
    globalSocketInstance = createGlobalSocketIO(finalUrl, {
      path: PATH,
      ...options,
    });
  }

  return globalSocketInstance;
}
